import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { catalogApi, type Catalog, type Category, type MarketplaceMapResult } from '../../api/catalog';

export function MarketplaceMappingPage() {
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [catalogId, setCatalogId] = useState('');
  const [productId, setProductId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [marketplace, setMarketplace] = useState('amazon');
  const [result, setResult] = useState<MarketplaceMapResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void catalogApi.listCatalogs().then((items) => {
      setCatalogs(items);
      if (items[0]?.id) setCatalogId(items[0].id);
    });
  }, []);

  useEffect(() => {
    if (!catalogId) return;
    void catalogApi.listCategories({ catalog_id: catalogId }).then(setCategories);
  }, [catalogId]);

  const map = async () => {
    if (!productId.trim() || !categoryId) {
      setError('Product ID and category are required');
      return;
    }
    setError('');
    try {
      const res = await catalogApi.marketplaceMap({
        product_id: productId.trim(),
        category_id: categoryId,
        marketplace,
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Mapping failed');
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <Link to="/catalog" className="text-sm text-slate-600 hover:underline">← Catalogs</Link>
      <h1 className="mt-4 text-2xl font-semibold">Marketplace Mapping</h1>
      <p className="mt-1 text-sm text-slate-600">Map internal categories to marketplace taxonomies.</p>
      <div className="mt-6 space-y-4 rounded-xl border bg-white p-6">
        <label className="block text-sm">
          Catalog
          <select value={catalogId} onChange={(e) => setCatalogId(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2">
            {catalogs.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          Product ID
          <input value={productId} onChange={(e) => setProductId(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2" />
        </label>
        <label className="block text-sm">
          Category
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2">
            <option value="">Select category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.path || c.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          Marketplace
          <select value={marketplace} onChange={(e) => setMarketplace(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2">
            <option value="amazon">Amazon</option>
            <option value="google">Google</option>
            <option value="flipkart">Flipkart</option>
            <option value="shopify">Shopify</option>
          </select>
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button type="button" onClick={() => void map()} className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white">
          Map to marketplace
        </button>
      </div>
      {result && (
        <div className="mt-6 rounded-xl border bg-white p-6">
          <h2 className="font-semibold">Mapping result</h2>
          <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            <div><dt className="text-slate-500">External ID</dt><dd>{result.external_id}</dd></div>
            <div><dt className="text-slate-500">External path</dt><dd>{result.external_path ?? '—'}</dd></div>
          </dl>
          {result.mappings?.length ? (
            <table className="mt-4 min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-600">
                  <th className="py-2">Marketplace</th>
                  <th className="py-2">External ID</th>
                  <th className="py-2">Path</th>
                </tr>
              </thead>
              <tbody>
                {result.mappings.map((m) => (
                  <tr key={m.id} className="border-b">
                    <td className="py-2">{m.marketplace}</td>
                    <td className="py-2">{m.external_id}</td>
                    <td className="py-2">{m.external_path ?? m.external_name ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      )}
    </div>
  );
}
